import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Phone, ShieldCheck, Hammer, MessagesSquare, Sparkles } from "lucide-react";
import { Reveal, MaskedLines, Overline } from "../components/Motion";
import { Marquee, RoofOptions, Process, ServiceAreas, EstimateSection } from "../components/Sections";
import { TrustStrip, Reviews, Videos } from "../components/Social";
import { INTRO_DELAY as D } from "../components/LogoIntro";
import { BeforeAfter } from "../components/BeforeAfter";
import { RealPhotos } from "../components/RealPhotos";
import { BIZ, IMG, SERVICES } from "../data/site";
import { Seo } from "../components/Seo";

const HERO_BG = [
  { src: IMG.crewHero, alt: "Good Home Improvement crew installing a new roof" },
  { src: IMG.crewSiding, alt: "A finished Good Home Improvement project" },
  { src: IMG.van, alt: "Good Home Improvement work van at a customer's home" },
];

const HeroSlides = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_BG.length), 6500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="absolute inset-0" data-testid="hero-slides">
      {HERO_BG.map((b, i) => (
        <motion.img key={b.src} src={b.src} alt={i === idx ? b.alt : ""} aria-hidden={i !== idx}
          initial={false} animate={{ opacity: i === idx ? 1 : 0, scale: i === idx ? 1.1 : 1 }}
          transition={{ opacity: { duration: 1.6, ease: "easeInOut" }, scale: { duration: 8, ease: "linear" } }}
          className="absolute inset-0 h-full w-full object-cover object-[60%_40%]" data-testid={`hero-slide-${i}`} />
      ))}
    </div>
  );
};

const HeroVideo = () => {
  const [ready, setReady] = useState(false);
  const [ok] = useState(() => !window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [size] = useState(() => (window.innerWidth < 768 ? "960" : "1920"));
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    const tryPlay = () => { const p = v.play(); if (p?.catch) p.catch(() => {}); };
    tryPlay();
    const onVis = () => { if (!document.hidden) tryPlay(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("touchstart", tryPlay, { once: true, passive: true });
    window.addEventListener("scroll", tryPlay, { once: true, passive: true });
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("touchstart", tryPlay);
      window.removeEventListener("scroll", tryPlay);
    };
  }, [ok]);
  if (!ok) return null;
  return (
    <video
      ref={ref}
      className={`hero-bg-video pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ${ready ? "opacity-100" : "opacity-0"}`}
      autoPlay muted loop playsInline preload="auto" controls={false} disablePictureInPicture disableRemotePlayback aria-hidden="true" tabIndex={-1}
      onPlaying={() => setReady(true)} data-testid="hero-video" data-playing={ready}
    >
      <source src={`/video/hero-${size}.mp4`} type="video/mp4" />
      <source src={`/video/hero-${size}.webm`} type="video/webm" />
    </video>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const nav = useNavigate();
  const [zip, setZip] = useState("");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.3]);
  const go = (e) => { e.preventDefault(); nav(`/get-quote?zip=${zip}`); };

  return (
    <section ref={ref} className="dark-ctx relative overflow-hidden bg-slate-950 text-white" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <HeroSlides />
        <HeroVideo />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/40" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-slate-950/85 to-transparent" />
      <motion.div style={{ opacity: fade }} className="wrap relative grid grid-cols-1 items-center gap-12 pb-14 pt-16 md:pb-20 md:pt-20 lg:min-h-[680px] lg:grid-cols-12">
        <div className="lg:col-span-8">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 0.1 }}><Overline light>Milwaukee Roofing · Siding · Gutters</Overline></motion.div>
          <h1 className="font-display text-[2.6rem] font-black uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-[5rem]" data-testid="hero-headline">
            <MaskedLines delay={D + 0.15} lines={["Your home,", <span key="a" className="text-amber-400">done right.</span>]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.6, duration: 0.8 }} className="mt-5 max-w-md text-sm leading-relaxed text-slate-200 md:text-base">
            Good Home Improvement — Milwaukee's BBB A-rated roofing, siding & gutter crew. Free estimates and drone inspections.
          </motion.p>
          <motion.form onSubmit={go} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.75, duration: 0.8 }} className="mt-7 flex max-w-md items-center gap-2 rounded-full bg-white p-1.5 pl-4 shadow-2xl" data-testid="hero-estimate-card">
            <MapPin className="h-5 w-5 shrink-0 text-slate-500" />
            <input inputMode="numeric" maxLength={5} required minLength={5} value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))} placeholder="ZIP code for a free quote" className="min-w-0 flex-1 bg-transparent py-2 text-slate-900 outline-none placeholder:text-slate-500" data-testid="hero-zip-input" />
            <button className="rounded-full bg-slate-900 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-700" data-testid="hero-zip-submit">Get quote</button>
          </motion.form>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.85, duration: 0.8 }} className="mt-4 flex flex-wrap gap-3">
            <a href={BIZ.tel} className="btn-ghost-light" data-testid="hero-call-button"><Phone className="h-4 w-4" />Call {BIZ.phone}</a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 1.05, duration: 0.8 }} className="mt-10 grid max-w-lg grid-cols-3 divide-x divide-white/20 border-t border-white/20 pt-6" data-testid="hero-stats">
            {[["A", "BBB rating"], ["5.0★", "Google reviews"], ["GAF", "Certified™"]].map(([b, s]) => (
              <div key={s} className="px-4 first:pl-0"><span className="font-display block text-3xl font-black leading-none md:text-4xl">{b}</span><span className="mt-1 block text-xs font-semibold uppercase tracking-widest text-slate-300">{s}</span></div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

const Intro = () => (
  <section className="bg-white py-24 md:py-32" data-testid="intro-section">
    <div className="wrap grid items-center gap-14 lg:grid-cols-12">
      <Reveal className="relative lg:col-span-6">
        <div className="grid grid-cols-5 gap-4">
          <div className="clip-frame col-span-5 overflow-hidden rounded-2xl sm:col-span-3"><img loading="lazy" decoding="async" src={IMG.after} alt="A finished Good Home Improvement project" className="aspect-[4/5] w-full object-cover transition-transform duration-[1.5s] hover:scale-105" /></div>
          <div className="hidden flex-col gap-4 sm:col-span-2 sm:flex">
            <div className="overflow-hidden rounded-2xl"><img loading="lazy" decoding="async" src={IMG.crewDetail} alt="Good Home Improvement crew on a roof" className="aspect-[3/4] w-full object-cover" /></div>
            <div className="flex flex-1 flex-col justify-end rounded-2xl bg-slate-100 p-5"><span className="font-display text-5xl font-black leading-none text-slate-900">7+</span><span className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-600">Years of roofing, siding & gutter experience</span></div>
          </div>
        </div>
        <div className="absolute -bottom-6 left-4 rounded-2xl bg-slate-900 p-5 text-white shadow-xl md:p-6">
          <span className="font-display block text-4xl font-black leading-none">FREE</span><span className="text-xs font-bold uppercase tracking-widest">Estimates</span>
        </div>
      </Reveal>
      <div className="lg:col-span-6">
        <Reveal><Overline>About Good Home</Overline>
          <h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">Milwaukee's exterior team… because neighbors refer us.</h2>
          <p className="mt-6 text-slate-600">{BIZ.name} is a locally owned Milwaukee exterior contractor serving homeowners across southeastern Wisconsin, with {BIZ.experience} years of hands-on experience. Roofing, siding and gutters are all we do — and we sweat the details other companies skip.</p></Reveal>
        <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
          {[{ t: "Meet Good Home", to: "/about" }, { t: "Explore our roofing", to: "/roofing" }, { t: "Request a free quote", to: "/get-quote" }].map((l, i) => (
            <Reveal key={l.t} delay={i * 0.06}>
              <Link to={l.to} className="group flex items-center justify-between py-5" data-testid={`intro-link-${i}`}>
                <span className="font-display text-2xl font-bold uppercase text-slate-900 transition-transform duration-300 group-hover:translate-x-2">{l.t}</span>
                <ArrowUpRight className="h-6 w-6 text-amber-600 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ServicesBento = () => (
  <section className="bg-slate-50 py-24 md:py-32" data-testid="services-section">
    <div className="wrap">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div><Overline>What we do</Overline><h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-slate-900 md:text-7xl">Our services</h2></div>
        <p className="max-w-sm text-slate-600">One team for the whole outside of your home — from the ridge cap down to the downspouts.</p>
      </Reveal>
      <div className="mt-14 grid gap-5 md:grid-cols-2 md:grid-rows-2 lg:h-[680px]">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.08} className={i === 0 ? "md:row-span-2" : ""}>
            <Link to={`/${s.slug}`} className="group relative block h-80 overflow-hidden rounded-2xl md:h-full" data-testid={`service-card-${s.slug}`}>
              <img loading="lazy" decoding="async" src={s.img} alt={s.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 text-white md:p-8">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">{s.kicker}</span>
                  <h3 className="font-display mt-1 text-5xl font-black uppercase leading-none md:text-6xl">{s.title}</h3>
                  <p className="mt-3 max-w-sm text-sm text-slate-200">{s.blurb}</p>
                </div>
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-amber-500 text-slate-950 transition-transform duration-500 group-hover:rotate-[-45deg]"><ArrowRight className="h-6 w-6" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Approach = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const pts = [
    { i: MessagesSquare, t: "We educate, you decide", d: "We show you photos of what we find and explain every option in plain language." },
    { i: Hammer, t: "Built for Midwest weather", d: "Proper ice & water protection, ventilation and flashing — the details that matter in Wisconsin." },
    { i: ShieldCheck, t: "Done right the first time", d: "Cheap work costs more when it has to be redone. We install it right so you don't pay twice." },
    { i: Sparkles, t: "Clean job site", d: "We protect your landscaping and clean up every nail before we leave." },
  ];
  return (
    <section ref={ref} className="dark-ctx relative overflow-hidden bg-slate-950 py-24 text-white md:py-36" data-testid="approach-section">
      <motion.img style={{ y }} src={IMG.storm} alt="" className="absolute inset-0 h-[124%] w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/70 to-slate-950" />
      <div className="wrap relative grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Overline light>Our unique approach</Overline>
          <h2 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl"><MaskedLines inView lines={["Projects", "done right."]} /></h2>
          <p className="mt-6 max-w-md text-slate-300">Our approach is different than most Wisconsin contractors. Our goal is to educate you about the project and let you make the decision. We're confident that when you know the facts, we're the best option.</p>
          <Link to="/contact" className="btn-amber mt-8" data-testid="approach-estimate-button">Get my free estimate <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
          {pts.map((p, idx) => (
            <Reveal key={p.t} delay={idx * 0.08}>
              <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur-md transition-colors duration-300 hover:border-amber-400/60">
                <p.i className="h-8 w-8 text-amber-400" />
                <h3 className="font-display mt-5 text-2xl font-bold uppercase">{p.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Gallery = () => {
  const shots = [IMG.after, IMG.farmhouse, IMG.hero, IMG.metal, IMG.colonial, IMG.crewSiding, IMG.siding, IMG.gutters];
  return (
    <section className="overflow-hidden bg-white py-24 md:py-32" data-testid="gallery-section">
      <div className="wrap"><Reveal><Overline>Homes we love</Overline><h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-slate-900 md:text-7xl">Curb appeal, built to last</h2></Reveal></div>
      <div className="gallery-track mt-14 flex w-max gap-5">
        {[...shots, ...shots].map((s, i) => (
          <div key={i} className="h-64 w-80 shrink-0 overflow-hidden rounded-2xl md:h-80 md:w-[28rem]" aria-hidden={i >= shots.length}>
            <img src={s} alt="Finished home exterior" className="h-full w-full object-cover" loading="lazy" />
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo title="Roofing, Siding & Gutters Milwaukee, WI | Good Home Improvement" description="GAF Certified, BBB A-rated roofer near you in Milwaukee, Wauwatosa, Brookfield, Oak Creek & Menomonee Falls. Roofs, siding, gutters. Free estimate: 414-396-7510." path="/" />
      <Hero />
      <Marquee items={["Good Home Improvement", "Roofing", "Siding", "Gutters", "Free Drone Inspections", "Free Estimates", "Milwaukee, WI"]} />
      <TrustStrip />
      <Intro />
      <ServicesBento />
      <BeforeAfter />
      <RealPhotos />
      <Reviews />
      <Approach />
      <Videos />
      <RoofOptions />
      <Process />
      <Gallery />
      <ServiceAreas />
      <EstimateSection />
    </main>
  );
}
